import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatSliderModule, MatSliderThumb } from '@angular/material/slider';
import { GoogleAnalyticsService } from '@mhe/reader/features/analytics';
import { FontResizerStore } from './state';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';

@Component({
  selector: 'reader-core-font-resizer-dialog',
  templateUrl: 'font-resizer-dialog.component.html',
  styleUrls: ['./font-resizer-dialog.component.scss'],
  host: { 'data-automation-id': 'font-resizer-dialog' },
  imports: [
    // angular
    A11yModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // material
    MatDialogModule,
    MatSliderModule,
    TranslateModule,
  ],
  providers: [FontResizerStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FontResizerDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ngSliderThumb') ngSliderThumb!: MatSliderThumb;
  @ViewChild('fontResizerContent') fontResizerContent!: ElementRef;

  readonly sliderControl = new FormControl(0);
  readonly max = 5;
  readonly min = -5;

  readonly val$ = this.sliderControl.valueChanges.pipe(
    map((val) => Number(val)),
    distinctUntilChanged(),
    shareReplay(1),
  );

  readonly increaseDisabled$ = this.val$.pipe(map((val) => val >= this.max));
  readonly decreaseDisabled$ = this.val$.pipe(map((val) => val <= this.min));

  private readonly destroy$ = new Subject<void>();
  private hasInitialAnnouncement = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { store: FontResizerStore },
    private readonly liveAnnouncer: LiveAnnouncer,
    private readonly translate: TranslateService,
    private readonly ga: GoogleAnalyticsService,
  ) { }

  ngOnInit(): void {
    // Set the sliderControl to the store's current value
    this.sliderControl.setValue(this.data.store.currentValue ?? 0);

    // Subscribe to changes to update the store
    this.val$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.data.store.valueChange$(val);
    });

    this.analyticsEmitChanges();
  }

  ngAfterViewInit(): void {
    this.ngSliderThumb.focus();
  }

  formatSliderLabel = (value: number): string => {
    // Convert slider value (-5 to 5) to percentage (0% to 200%)
    const percentage = 100 + value * 20;

    if (!this.hasInitialAnnouncement) {
      this.hasInitialAnnouncement = true;
      const minPercentage = 100 + this.min * 20; // 0%
      const maxPercentage = 100 + this.max * 20; // 200%
      // Detailed announcement upon initial focus
      return this.translate.instant('font-resizer.announce_current', {
        percentage,
        min: minPercentage,
        max: maxPercentage,
      });
    } else {
      // Shorter announcement for subsequent updates
      return this.translate.instant('font-resizer.announce_set_to', {
        percentage,
      });
    }
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fontsizeValueChanged(event): void {
    this.sliderControl.setValue(event.value);
  }

  increase(): void {
    let val = this.sliderControl.value as number;
    val += 1;

    this.sliderControl.setValue(val);
    this.announceValueChange(val);
  }

  decrease(): void {
    let val = this.sliderControl.value as number;
    val -= 1;

    this.sliderControl.setValue(val);
    this.announceValueChange(val);
  }

  /** a11y */
  private announceValueChange(value: any): void {
    const percentage = 100 + value * 20;
    const currentVal = this.translate.instant('font-resizer.announce_set_to', {
      percentage,
    });
    void this.liveAnnouncer.announce(currentVal);
  }

  /** analytics */
  private analyticsEmitChanges(): void {
    const valHistory$ = this.val$.pipe(
      startWith(false),
      pairwise(),
      filter(([p, _c]) => Boolean(p)),
    );

    const eventAction$ = valHistory$.pipe(
      map(([p, c]) => ({
        eventAction: p < c ? 'Increase' : 'Decrease',
        eventValue: Number(c),
      })),
    );

    eventAction$.pipe(takeUntil(this.destroy$)).subscribe((eventParams) => {
      this.ga.event({ eventCategory: 'Font Resizer', ...eventParams });
    });
  }
}
